import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchIcon, ShoppingBagIcon, PlusIcon, MinusIcon, XIcon, StoreIcon } from 'lucide-react';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { MetadataItem } from '../../../shared/ui/MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
interface Shop {
  id: number;
  name: string;
  description: string;
  logo: string;
}
interface Product {
  id: number;
  shopId: number;
  name: string;
  price: number;
  description: string;
  image: string;
  category: string;
}
interface CartItem {
  productId: number;
  quantity: number;
  product: Product;
}
interface ShopProductsPaymentProps {
  onExpiryDateChange?: (date: string) => void;
  onCreateLink?: () => void;
}
const shops: Shop[] = [{
  id: 1,
  name: 'Urban Outfitters',
  description: 'Contemporary clothing and lifestyle products',
  logo: 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
}, {
  id: 2,
  name: 'Tech Haven',
  description: 'Latest electronics and gadgets',
  logo: 'https://images.unsplash.com/photo-1550009158-9ebf69173e03?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
}, {
  id: 3,
  name: 'Home Essentials',
  description: 'Quality products for your home',
  logo: 'https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
}];
const products: Product[] = [{
  id: 101,
  shopId: 1,
  name: 'Basic T-Shirt',
  price: 599,
  description: 'Comfortable cotton t-shirt',
  image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Clothing'
}, {
  id: 102,
  shopId: 1,
  name: 'Slim Fit Jeans',
  price: 1499,
  description: 'Classic blue denim jeans',
  image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Clothing'
}, {
  id: 103,
  shopId: 1,
  name: 'Leather Wallet',
  price: 899,
  description: 'Genuine leather wallet',
  image: 'https://images.unsplash.com/photo-1627123424574-724758594e93?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Accessories'
}, {
  id: 201,
  shopId: 2,
  name: 'Premium Headphones',
  price: 2699,
  description: 'Noise cancelling headphones',
  image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Electronics'
}, {
  id: 202,
  shopId: 2,
  name: 'Smart Watch',
  price: 5999,
  description: 'Fitness and health tracking',
  image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Electronics'
}, {
  id: 203,
  shopId: 2,
  name: 'Wireless Earbuds',
  price: 3899,
  description: 'True wireless Bluetooth earbuds',
  image: 'https://images.unsplash.com/photo-1572569511254-d8f925fe2cbb?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Electronics'
}, {
  id: 301,
  shopId: 3,
  name: 'Ceramic Coffee Mug',
  price: 389,
  description: 'Handmade ceramic mug',
  image: 'https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Kitchen'
}, {
  id: 302,
  shopId: 3,
  name: 'Decorative Throw Pillow',
  price: 749,
  description: 'Soft decorative pillow',
  image: 'https://images.unsplash.com/photo-1592078615290-033ee584e267?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Home Decor'
}, {
  id: 303,
  shopId: 3,
  name: 'Scented Candle Set',
  price: 1049,
  description: 'Set of 3 scented soy candles',
  image: 'https://images.unsplash.com/photo-1602523069662-0b1ea6503f14?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Home Decor'
}];
const formatPrice = (amount: number) => formatCurrencyAmount(amount, {
  fractionDigits: 0
});
export function ShopProductsPayment({
  onExpiryDateChange,
  onCreateLink
}: ShopProductsPaymentProps) {
  const {
    t
  } = useTranslation('payments');
  const [searchTerm, setSearchTerm] = useState('');
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedShopId, setSelectedShopId] = useState<number>(1);
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
  const filteredProducts = products.filter(p => p.shopId === selectedShopId && (searchTerm === '' || p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.category.toLowerCase().includes(searchTerm.toLowerCase())));
  const addToCart = (product: Product) => {
    setCart(prev => {
      const existing = prev.find(i => i.productId === product.id);
      if (existing) {
        return prev.map(i => i.productId === product.id ? {
          ...i,
          quantity: i.quantity + 1
        } : i);
      }
      return [...prev, {
        productId: product.id,
        quantity: 1,
        product
      }];
    });
  };
  const removeFromCart = (productId: number) => {
    setCart(prev => prev.filter(i => i.productId !== productId));
  };
  const updateQuantity = (productId: number, qty: number) => {
    if (qty < 1) return;
    setCart(prev => prev.map(i => i.productId === productId ? {
      ...i,
      quantity: qty
    } : i));
  };
  const totalAmount = cart.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const totalItems = cart.reduce((s, i) => s + i.quantity, 0);
  const handleExpiryDateChange = (date: string) => {
    setExpiryDate(date);
    onExpiryDateChange?.(date);
  };
  return <div className="flex gap-5 items-start" data-id="element-8980" data-mp="2ma5l3ilxluyu">
      {/* ─── LEFT COLUMN ─── */}
      <div className="flex-1 space-y-5 min-w-0" data-id="element-8981" data-mp="1yj05n4jwl4p2">
        {/* ─── SHOP & PRODUCTS ─── */}
        <div className="bg-white rounded-xl border border-gray-200" data-id="element-8982" data-mp="1tkovespp4s6b">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-8983" data-mp="1tmjjuh06f2f5">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-8984" data-mp="27l9iw26k3c65">
              <StoreIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-8985" data-mp="23id30j8ehu5w" />
              {t('components.ShopProductsPayment.shop_products')}
            </h2>
            <p className="text-xs text-gray-500 mt-1" data-id="element-8986" data-mp="3j1g6e2di9bko">
              {t('components.ShopProductsPayment.select_a_shop_and_add_products')}
            </p>
          </div>

          {/* Shop selection — edge-to-edge rows */}
          <div data-id="element-8987" data-mp="3rhf6i396ia9b">
            {shops.map((shop, idx) => <div key={shop.id} className={`flex items-center px-6 py-4 cursor-pointer transition-colors ${selectedShopId === shop.id ? 'bg-[#E2231A]/5' : 'hover:bg-gray-50'} ${idx < shops.length - 1 ? 'border-b border-gray-200' : ''}`} onClick={() => setSelectedShopId(shop.id)} data-id="element-8988" data-mp="3cj4ovxuagdfa" data-mpi={idx}>
                <input type="radio" name="shop" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A]/20 border-gray-300 flex-shrink-0" checked={selectedShopId === shop.id} onChange={() => setSelectedShopId(shop.id)} data-id="element-8989" data-mp="e0fytv0u5jku" />

                <div className="ml-4 flex items-center flex-1 min-w-0" data-id="element-8990" data-mp="rzxqci5inaxw">
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-8991" data-mp="wsqv360zij9z">
                    <img src={shop.logo} alt={shop.name} className="w-full h-full object-cover" data-id="element-8992" data-mp="2svwzmhd0o1ei" />
                  </div>
                  <div className="ml-3 min-w-0" data-id="element-8993" data-mp="2k3bi14l4g0r9">
                    <p className="text-sm font-medium text-gray-900 truncate" data-id="element-8994" data-mp="2f3yvym22qxpq">
                      {shop.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate" data-id="element-8995" data-mp="3bsy29vj3lpfa">
                      {shop.description}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>

          {/* Search */}
          <div className="px-6 py-4 border-t border-gray-200" data-id="element-8996" data-mp="2xu6usryqr204">
            <div className="relative" data-id="element-8997" data-mp="2xtoof2id77eh">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8998" data-mp="1re4lgb589l8n">
                <SearchIcon size={16} className="text-gray-400" data-id="element-8999" data-mp="1mf1hku0zhl29" />
              </div>
              <input type="text" className={`${inputClass} pl-10`} placeholder={t('components.ShopProductsPayment.attrs.search_products')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-9000" data-mp="1mhe3tjdryhl4" />
            </div>
          </div>

          {/* Product grid */}
          <div className="px-6 pb-6" data-id="element-9001" data-mp="1r1ttogevq1er">
            {filteredProducts.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-9002" data-mp="1v3ovdm0q2gt2">
                {filteredProducts.map(product => {
              const inCart = cart.find(i => i.productId === product.id);
              return <div key={product.id} className="border border-gray-200 rounded-lg p-3 flex gap-3" data-id="element-9003" data-mp="29zemfykrlk4n">
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-9004" data-mp="ao3qdbvl3h66">
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-9005" data-mp="fmopt930hwtk" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-9006" data-mp="3m61k1tl4wvni">
                        <div className="flex items-start justify-between gap-2" data-id="element-9007" data-mp="3m8ntjyrl5l8d">
                          <p className="text-sm font-medium text-gray-900 truncate" data-id="element-9008" data-mp="3r6pd0ksxbmkd">
                            {product.name}
                          </p>
                          <p className="text-sm font-semibold text-gray-900 flex-shrink-0" data-id="element-9009" data-mp="4xe92a2sce80">
                            {formatPrice(product.price)}
                          </p>
                        </div>
                        <p className="text-[11px] text-gray-400 mt-0.5" data-id="element-9010" data-mp="dpg761pdtif4">
                          {product.category}
                        </p>
                        <div className="mt-2" data-id="element-9011" data-mp="inri6frexyyx">
                          <button type="button" onClick={() => addToCart(product)} className="px-2.5 py-1 bg-[#E2231A]/10 text-[#9F140F] hover:bg-[#E2231A]/20 rounded-md text-xs font-medium flex items-center transition-colors" data-id="element-9012" data-mp="nnvxw3khypo3">
                            <PlusIcon size={12} className="mr-1" data-id="element-9013" data-mp="nwitsshxhb03" />
                            {inCart ? `Add (${inCart.quantity})` : 'Add'}
                          </button>
                        </div>
                      </div>
                    </div>;
            })}
              </div> : <div className="text-center py-8" data-id="element-9014" data-mp="16t27zw7l9p3i">
                <SearchIcon size={20} className="mx-auto text-gray-400 mb-2" data-id="element-9015" data-mp="16rr47we58hce" />
                <p className="text-sm font-medium text-gray-700" data-id="element-9016" data-mp="12qnpjd7u0rcr">
                  {t('components.ShopProductsPayment.no_products_found')}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-9017" data-mp="1uiwiw88cl3l4">
                  {t('components.ShopProductsPayment.try_a_different_search_term')}
                </p>
              </div>}
          </div>
        </div>

        {/* ─── ADDITIONAL OPTIONS ─── */}
        <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} data-id="element-9018" data-mp="3f171nzpxkrma" />
      </div>

      {/* ─── RIGHT COLUMN — ORDER SUMMARY ─── */}
      <div className="w-80 flex-shrink-0 sticky top-6" data-id="element-9019" data-mp="3b03m7ebsoxll">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-9020" data-mp="2w2b9sv1jopbm">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-9021" data-mp="31162copbds8x">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-9022" data-mp="311o420uqwyo8">
              <ShoppingBagIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-9023" data-mp="ubzudfhcb6gt" />
              {t('components.ShopProductsPayment.order_summary')}
            </h3>
          </div>

          {cart.length > 0 ? <>
              <div data-id="element-9024" data-mp="2js3ftq10p6di">
                {cart.map((item, idx) => <div key={item.productId} className={`px-6 py-3 flex items-center gap-3 ${idx < cart.length - 1 ? 'border-b border-gray-200' : ''}`} data-id="element-9025" data-mp="2ets7xkua95xx" data-mpi={idx}>
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-9026" data-mp="2oq518hlx1hs5">
                      <img src={item.product.image} alt={item.product.name} className="w-full h-full object-cover" data-id="element-9027" data-mp="32h0f68b9up2l" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-9028" data-mp="3ce4zs3pgycha">
                      <div className="flex items-start justify-between gap-1" data-id="element-9029" data-mp="37iponjiuiuok">
                        <p className="text-sm font-medium text-gray-900 truncate" data-id="element-9030" data-mp="1i1om4hucfw2e">
                          {item.product.name}
                        </p>
                        <button type="button" onClick={() => removeFromCart(item.productId)} aria-label="Remove from cart" className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0" data-id="element-9031" data-mp="1d5q379rsb69d">
                          <XIcon size={14} aria-hidden="true" data-id="element-9032" data-mp="1ticu32rzkop9" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-1" data-id="element-9033" data-mp="vv40ekhu09c6">
                        <div className="flex items-center border border-gray-200 rounded-md" data-id="element-9034" data-mp="10smd8lyyz8pg">
                          <button type="button" onClick={() => updateQuantity(item.productId, item.quantity - 1)} className="px-1.5 py-0.5 text-gray-500 hover:text-gray-700 transition-colors" data-id="element-9035" data-mp="15rp85dl8jyjp">
                            <MinusIcon size={12} data-id="element-9036" data-mp="i0jv166ojkd3" />
                          </button>
                          <span className="px-2 text-xs font-medium text-gray-900" data-id="element-9037" data-mp="2vc4oqx6069dq">
                            {item.quantity}
                          </span>
                          <button type="button" onClick={() => updateQuantity(item.productId, item.quantity + 1)} className="px-1.5 py-0.5 text-gray-500 hover:text-gray-700 transition-colors" data-id="element-9038" data-mp="2mbgaq2gm090u">
                            <PlusIcon size={12} data-id="element-9039" data-mp="23lrjajbyvaq8" />
                          </button>
                        </div>
                        <p className="text-xs font-medium text-gray-900" data-id="element-9040" data-mp="1ymx4dsh4e6be">
                          {formatPrice(item.product.price * item.quantity)}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div>

              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50/50" data-id="element-9041" data-mp="1f0v8ofa3x9pr">
                <div className="flex items-center justify-between" data-id="element-9042" data-mp="1f37z7gtxksef">
                  <span className="text-sm text-gray-500" data-id="element-9043" data-mp="1k1j91rk872wn">
                    {t('components.renderSchedulePreview.total')}
                  </span>
                  <span className="text-lg font-bold text-gray-900" data-id="element-9044" data-mp="1a1im7vn5e4np">
                    {formatPrice(totalAmount)}
                  </span>
                </div>
                <p className="text-[11px] text-gray-400 mt-1" data-id="element-9045" data-mp="1xs4spihi7gvl">
                  {totalItems} {t('components.ShopProductsPayment.item')}
                  {totalItems !== 1 ? 's' : ''}
                </p>
              </div>
            </> : <div className="px-6 py-10 text-center" data-id="element-9046" data-mp="1t04qfjdyobvu">
              <ShoppingBagIcon size={20} className="mx-auto text-gray-400 mb-2" data-id="element-9047" data-mp="1syjr7ggpc4rn" />

              <p className="text-sm font-medium text-gray-700" data-id="element-9048" data-mp="2lgp5j5betn6k">
                {t('components.ProductSelectionPayment.no_products_selected')}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-9049" data-mp="a05h2ormkhlm">
                {t('components.ShopProductsPayment.add_products_from_the_shop')}
              </p>
            </div>}
        </div>

        {/* Create link button */}
        <button type="button" disabled={cart.length === 0} onClick={onCreateLink} className={`w-full mt-4 px-6 py-2.5 text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center justify-center ${cart.length > 0 ? 'bg-[#E2231A] hover:bg-[#C41B14] text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-9050" data-mp="esyjh5zmfduj">
          {t('components.ShopProductsPayment.create_payment_link')}
        </button>
      </div>
    </div>;
}